import React, { useState } from 'react';
import styled from 'styled-components';
import { parseRiskTolerance, plotLineGradient } from 'src/components/charts/util';
import { Spacer } from 'src/common';
import { Assessment, AssessmentVersion } from 'src/api/types';
import { Head } from '../Head';
import Chart from 'src/components/charts/Chart';
import { useStateSelector } from 'src/redux';
import { ComparisonAssessmentQuery } from 'src/graphql/_generated';

const Div = styled.div`
  .lec {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

type AssessmentData = NonNullable<ComparisonAssessmentQuery['assessmentA']>;

interface ChartsProps {
  dataA: AssessmentData;
  dataB: AssessmentData;
  versionA?: AssessmentVersion;
  versionB?: AssessmentVersion;
  assessmentA: {
    seriesEnabled: boolean;
    showProjected: boolean;
  };
  assessmentB: {
    seriesEnabled: boolean;
    showProjected: boolean;
  };
}

export const Charts: React.FC<ChartsProps> = ({ dataA, dataB, versionA, versionB, assessmentA, assessmentB }) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [isLogarithmic, setIsLogarithmic] = useState(true);
  const [isArea, setIsArea] = useState(true);

  // Standard loss-exceedance fills, matching the single-assessment chart (VersionedChart).
  const fillBase = 'rgba(2, 3, 51, 0.07)';
  const fillComplete = 'rgba(100, 149, 237, 0.07)';
  const fillLive = 'rgba(230, 46, 97, 0.07)';

  // Helper function to get min/max from GraphQL scenario data
  const getMinMaxFromGqlScenarios = (
    scenarios: AssessmentData['scenarios'],
    riskType: 'live' | 'base' | 'projected',
  ) => {
    const riskField = riskType === 'live' ? 'liveRisk' : riskType === 'base' ? 'baseRisk' : 'projectedRisk';
    const values = scenarios.map((s) => s[riskField]?.expectedLoss || 0).filter((v) => v > 0);
    return {
      upper: Math.max(...values, 1),
      lower: Math.min(...values, 1),
    };
  };

  // Both versions use the standard loss-exceedance colours from the single-assessment chart.
  // Version B (the comparison version) is rendered at 0.5 opacity so it reads as the faded
  // overlay against the solid Version A.
  const charts = [
    {
      isEnabled: assessmentA.seriesEnabled,
      params: {
        data: dataA.AssessmentLiveRisk?.chart || [],
        upper: getMinMaxFromGqlScenarios(dataA.scenarios, 'live').upper,
        lower: getMinMaxFromGqlScenarios(dataA.scenarios, 'live').lower,
        name: 'Loss Exceedance A',
        color: plotLineGradient,
        fillColor: isArea ? 'transparent' : fillLive,
        legendId: 'col_a_live',
        lineWidth: 4,
      },
    },
    {
      isEnabled: assessmentA.seriesEnabled && assessmentA.showProjected,
      params: {
        data: dataA.AssessmentBaseRisk?.chart || [],
        upper: getMinMaxFromGqlScenarios(dataA.scenarios, 'base').upper,
        lower: getMinMaxFromGqlScenarios(dataA.scenarios, 'base').lower,
        name: 'Loss Exceedance A base',
        color: colors.prussianBlue,
        fillColor: isArea ? 'transparent' : fillBase,
        legendId: 'col_a_base',
        lineWidth: 4,
      },
    },
    {
      isEnabled: assessmentA.seriesEnabled && assessmentA.showProjected,
      params: {
        data: dataA.AssessmentProjectedRisk?.chart || [],
        upper: getMinMaxFromGqlScenarios(dataA.scenarios, 'projected').upper,
        lower: getMinMaxFromGqlScenarios(dataA.scenarios, 'projected').lower,
        name: 'Loss Exceedance A complete',
        color: colors.brightBlue,
        fillColor: isArea ? 'transparent' : fillComplete,
        legendId: 'col_a_complete',
        lineWidth: 4,
      },
    },
    // -----------------------------------B (faded: opacity 0.5)
    {
      isEnabled: assessmentB.seriesEnabled,
      params: {
        data: dataB.AssessmentLiveRisk?.chart || [],
        upper: getMinMaxFromGqlScenarios(dataB.scenarios, 'live').upper,
        lower: getMinMaxFromGqlScenarios(dataB.scenarios, 'live').lower,
        name: 'Loss Exceedance B',
        color: plotLineGradient,
        fillColor: isArea ? 'transparent' : fillLive,
        legendId: 'col_b_live',
        lineWidth: 4,
        opacity: 0.5,
      },
    },
    {
      isEnabled: assessmentB.seriesEnabled && assessmentB.showProjected,
      params: {
        data: dataB.AssessmentBaseRisk?.chart || [],
        upper: getMinMaxFromGqlScenarios(dataB.scenarios, 'base').upper,
        lower: getMinMaxFromGqlScenarios(dataB.scenarios, 'base').lower,
        name: 'Loss Exceedance B base',
        color: colors.prussianBlue,
        fillColor: isArea ? 'transparent' : fillBase,
        legendId: 'col_b_base',
        lineWidth: 4,
        opacity: 0.5,
      },
    },
    {
      isEnabled: assessmentB.seriesEnabled && assessmentB.showProjected,
      params: {
        data: dataB.AssessmentProjectedRisk?.chart || [],
        upper: getMinMaxFromGqlScenarios(dataB.scenarios, 'projected').upper,
        lower: getMinMaxFromGqlScenarios(dataB.scenarios, 'projected').lower,
        name: 'Loss Exceedance B complete',
        color: colors.brightBlue,
        fillColor: isArea ? 'transparent' : fillComplete,
        legendId: 'col_b_complete',
        lineWidth: 4,
        opacity: 0.5,
      },
    },
    // -----------------------------------Risk Tolerance
    {
      isEnabled: assessmentA.seriesEnabled,
      params: {
        data: parseRiskTolerance(dataA.riskTolerance || ''),
        name: 'Risk Tolerance A',
        type: 'spline',
        dashStyle: 'ShortDash',
        color: colors.cflowerBlue,
        legendId: 'leg-car-a',
        isRiskTolerance: true,
      },
    },
    {
      isEnabled: assessmentB.seriesEnabled,
      params: {
        data: parseRiskTolerance(dataB.riskTolerance || ''),
        name: 'Risk Tolerance B',
        type: 'spline',
        dashStyle: 'ShortDash',
        color: colors.cflowerBlue,
        legendId: 'leg-car-b',
        isRiskTolerance: true,
        opacity: 0.5,
      },
    },
  ].map((el) => {
    return { ...el.params, data: el.isEnabled ? el.params.data : [] };
  });

  return (
    <Div>
      <Head
        dataA={dataA}
        dataB={dataB}
        versionA={versionA}
        versionB={versionB}
        isLogarithmic={isLogarithmic}
        setIsLogarithmic={setIsLogarithmic}
        isArea={isArea}
        setIsArea={setIsArea}
        showProjectedA={assessmentA.showProjected}
        showProjectedB={assessmentB.showProjected}
      />
      <Spacer $px={10} />
      <div className="paddings">
        <Chart isLogarithmic={isLogarithmic} input={charts} persistLegend={false} />
      </div>
    </Div>
  );
};
